import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AppDispatch, RootState } from 'core/store/reduxStore';

import { getAlertsResponseMapper } from 'modules/ac2/mappers/alertsMapper';
import { t } from 'i18next';
import { 
    getAlertsRequestRecursion, 
    getAlertsUpdatesRequestRecursion, 
    setAlertStatusRequestService, 
    assignAlertRequestService, 
    getAlertsByPage, 
    getConformanceMetrics
} from "modules/ac2/services/alertService";
import { IRowProps } from 'core/components/global/tables/datagrid/dataGrid.type';
import { IHeadCell } from 'core/components/global/tables/datagrid/dataGridHeader/datagridHeader';
import { Toast as showToast } from "core/components/global/toast/toast";
import { alertHeaders, userHeaders } from './headers';

interface AlertsState {
  pageEvents: IRowProps[],
  userEvents: IRowProps[],
  weeklyEvents: IRowProps[],
  areUserEventsLoading: boolean,
  alertHeaders: IHeadCell[],
  userHeaders: IHeadCell[],
  newestStateEventTs: number,
  isFetching: boolean,
  assignedToOrg: number,
  criticalToday: number,
  lastUpdatedTs: number,
  previewWidth: number,
  selectedRow: string,
  openSideModal: any,
  totalRowCount: number,
  isExportLoading: boolean,
  isPageLoading: boolean,
  conformanceMetrics: [],
  frameworkData: {
    descriptions: any[],
    selectedDescription: string,
    selectedRawRuleName: string,
    selectedFramework: string,
    selectedSectionTitle: string
  }
}

const initialState: AlertsState = {
  isExportLoading: false,
  totalRowCount: 0,
  pageEvents: [],
  userEvents: [],
  weeklyEvents: [],
  areUserEventsLoading: false,
  selectedRow: "",
  openSideModal: undefined,
  previewWidth: 40,
  conformanceMetrics: [],
  alertHeaders: alertHeaders,
  userHeaders: userHeaders,
  newestStateEventTs: new Date((new Date()).getTime() - (7 * 24 * 60 * 60 * 1000)).getTime(),
  isFetching: true,
  assignedToOrg: 0,
  criticalToday: 0,
  lastUpdatedTs: 0,
  isPageLoading: false,
  frameworkData: {
    descriptions: [],
    selectedDescription: "",
    selectedRawRuleName: "",
    selectedFramework: "",
    selectedSectionTitle: ""
  }
};

/**
 * Adds messages to an existing or new conversation
 */
export const setAlertStatus = createAsyncThunk<{aFAccessToken: string, status: string, id: string}, {aFAccessToken: string, status: string, id: string}, { state: RootState, dispatch: AppDispatch }>(
  'setAlertStatus',
  async ({aFAccessToken, status, id}: {aFAccessToken: string, status: string, id: string}, { getState, dispatch }: { getState: () => RootState, dispatch: AppDispatch }) => {
    // Push the data to the DBs
    const config = { 'Authorization': aFAccessToken || '', 'Content-Type': 'application/json' };

    try {
      // Make the HTTP request to set the status
      const response = await setAlertStatusRequestService(status, id, config);
      
      // Decide to show a toast for failure or save the data
      if (response.status >= 300) {
        console.error(`${response.status} HTTP Response; error assigning status ${status} to ${id}`);
        showToast(`${response.status} HTTP Response; error assigning status ${status} to ${id}`, "error");
      } else {

        // Add the data so the UI can show the new data
        dispatch(putPageEvent({'value': status, 'id': id, 'type': 'status'}));
      }
    } catch (error) {
      showToast(`Error assigning ${id} to ${status}`, "error");
    }
    return {'status': status, 'id': id, aFAccessToken: ""};
  }
);

/**
 * Adds messages to an existing or new conversation
 */
export const assignUserToAlert = createAsyncThunk<{aFAccessToken: string, email: string, id: string}, {aFAccessToken: string, email: string, id: string}, { state: RootState, dispatch: AppDispatch }>(
  'assignUserToAlert',
  async ({aFAccessToken, email, id}: {aFAccessToken: string, email: string, id: string}, { getState, dispatch }: { getState: () => RootState, dispatch: AppDispatch }) => {
    // Push the data to the DBs
    const config = { 'Authorization': aFAccessToken || '', 'Content-Type': 'application/json' };
    
    try {
      // Make the HTTP request to set the assigned user
      const response = await assignAlertRequestService(email, id, config);

      // Decide to show a toast for failure or save the data
      if (response.status === 200) {

        // Add the data so the UI can show the new data
        dispatch(putPageEvent({'value': email, 'id': id, 'type': 'email'}));
      } else {
        console.error(`${response.status} HTTP Response; error assigning ${email} to ${id}`);
        showToast(`Error assigning ${email} to ${id}`, "error");
      }
    } catch (error) {
      showToast(`Error assigning ${email} to ${id}`, "error");
    }
    return {'email': email, 'id': id, aFAccessToken: ""};
  }
);

export const setWeeklyAlerts = createAsyncThunk<void, string, { state: RootState, dispatch: AppDispatch }>(
  'setWeeklyAlerts',
  async (aFAccessToken, { getState, dispatch }: { getState: () => RootState, dispatch: AppDispatch }) => {
    const state = getState().alerts;
    const config = { 'Authorization': aFAccessToken || '', 'Content-Type': 'application/json' };
    let response = { 'records': [], 'totalCount': 0, 'newestStateEventTs': 0, 'offset': 0 };
    let totalCount = 0;
    let offset = 0;

    // Get the day of 1 week ago: 7 days * 24 hours * 60 minutes * 60 seconds * 1000 miliseconds
    // TODO fix date once demos are over 
    const now = new Date();
    const oneWeekAgo = new Date(now.getTime() - (7 * 24 * 60 * 60 * 1000)).getTime();
    
    // Fetch if no data has been fetched before
    if (state.weeklyEvents.length === 0) {

      // Set a var to collect the offset data as we paginate
      let allRecords = [];
      do {
        const response_data = await getAlertsRequestRecursion(config, 1000, offset, oneWeekAgo);
        if (response_data) {
          // If the request was successful proceed
          response = response_data.data;
          
          // Process if there are records left in the offset/pagingation otherwise break
          if (response && response.records && response.records.length > 0) {
            allRecords = [...allRecords, ...response.records];
            totalCount += response.records.length;
            offset = response.offset
          } else {
            // No more records to fetch
            break;
          }
        } else {
          // No more records to fetch
          break;
        }
      } while (totalCount < response.totalCount);

      // Pagination complete: now update the records locally, if there is something to update
      if (allRecords.length > 0) {
        dispatch(
          setWeeklyEvents(
            getAlertsResponseMapper(t, allRecords)
          )
        )
      }
    }

    // Update the records with changes made to the db since the last fetch
    const response_data = await getAlertsUpdatesRequestRecursion(config, Math.round(state.lastUpdatedTs/1000));
    if (response_data.status === 200 && response_data.data && response_data.data.length > 0) {
      dispatch(setLastUpdated(Date.now()));
      dispatch(updateWeeklyEvents(getAlertsResponseMapper(t, response_data.data)))
    }    
  }
);


export const setConformanceData = createAsyncThunk<void, string, { state: RootState, dispatch: AppDispatch }>(
  'setConformanceData',
  async (aFAccessToken, { getState, dispatch }: { getState: () => RootState, dispatch: AppDispatch }) => {
    const state = getState().alerts;
    const config = { 'Authorization': aFAccessToken || '', 'Content-Type': 'application/json' };
    
    // If the request was successful proceed
    const response = await getConformanceMetrics(config);
    
    // Decide to show a toast for failure or save the data
    if (response && response.status === 200) {
      console.log('Conformance data received:', response.data);
      // Add the data so the UI can show the new data
      dispatch(setConformanceMetrics(response.data.compliance_metrics));
    } else {
      showToast(`Error fetching the Framework Metrics`, "error");
    } 
  }
);

export const updatePageAlerts = createAsyncThunk<void, string, { state: RootState, dispatch: AppDispatch }>(
  'updatePageAlerts',
  async (aFAccessToken, { getState, dispatch }: { getState: () => RootState, dispatch: AppDispatch }) => {
    const state = getState().alerts;
    const config = { 'Authorization': aFAccessToken || '', 'Content-Type': 'application/json' };
    let recursionNewestTs = state.newestStateEventTs > 0 ? state.newestStateEventTs: 0;
    let response = { 'records': [], 'totalCount': 0, 'newestStateEventTs': 0, 'offset': 0 };
    let totalCount = 0;
    let offset = 0;
    
    // Fetch if no data has been fetched before
    if (state.pageEvents.length === 0) {

      // Set a var to collect the offset data as we paginate
      let allRecords = [];
      do {
        const response_data = await getAlertsRequestRecursion(config, 1000, offset, state.newestStateEventTs);
        if (response_data) {
          // If the request was successful proceed
          response = response_data.data;
          
          // Process if there are records left in the offset/pagingation otherwise break
          if (response && response.records && response.records.length > 0) {
            allRecords = [...allRecords, ...response.records];
            totalCount += response.records.length;

            // Update recursionOldestTs for the next request
            if (Number(response.newestStateEventTs) > 0) {
              /**
               * Protect yourself from the timestamps being changed from seconds to milliseconds 
               * or vice versa.
               * 1) Find the length of what today's millisecond length should be
               * 2) compare with the length of the returned timestamp
               * 3) if returned < todays millisecond length, the returned is in seconds so x by 1000
               * to convert returned from seconds to milliseconds
              */ 
              // Find the length of what today's millisecond length should be
              const charCount = (new Date()).getTime().toString().length;
              if (response.newestStateEventTs.toString().length < charCount){
                // Convert to milliseconds
                recursionNewestTs = response.newestStateEventTs * 1000
              } else {
                // Already in milliseconds
                recursionNewestTs = response.newestStateEventTs;
              }
            }

            offset = response.offset
          } else {
            // No more records to fetch
            break;
          }
        } else {
          // No more records to fetch
          break;
        }
      } while (totalCount < response.totalCount);
      
      // Pagination complete: now update the records locally, if there is something to update
      if (allRecords.length > 0) {
        dispatch(
          setPageEvents(
            getAlertsResponseMapper(t, allRecords)
          )
        )
      }
    }

    // Update the records with changes made to the db since the last fetch
    const response_data = await getAlertsUpdatesRequestRecursion(config, Math.round(state.lastUpdatedTs/1000));
    if (response_data.status === 200 && response_data.data && response_data.data.length > 0) {
      dispatch(setLastUpdated(Date.now()));
      dispatch(updateEvents(getAlertsResponseMapper(t, response_data.data)))
    }
    
    dispatch(setRecursionNewestTs(recursionNewestTs));
  }
);

export const setAlertsByPage = createAsyncThunk<void, {target: string, aFAccessToken: string, size: number, page: number, order: string, orderBy: string, filters: object, search: string}, { state: RootState, dispatch: AppDispatch }>(
  'setAlertsByPage',
  async ({target, aFAccessToken, size, page, order, orderBy, filters, search}, { getState, dispatch }: { getState: () => RootState, dispatch: AppDispatch }) => {
    const t = (key: string) => key; // Simple translation function stub
    const config = { 'Authorization': aFAccessToken || '', 'Content-Type': 'application/json' };    

    // Make an HTTP request to get the page of data
    const response = await getAlertsByPage(config, size, page, order, orderBy, filters, search);

    if (response) {
      // Process if there are records left in the offset/pagingation otherwise break
      if (response && response.data.records) {

        // Remap the data into the format we expect for the UI tables
        const records = getAlertsResponseMapper(t, response.data.records);
        
        switch (target) {
          case 'home':
            // User home page (Analyst's Page)
            dispatch(

              // Update the UI to reflect the returned data
              setUserEvents(
                records
              )
            )

            dispatch(
              setTotalRowCount(response.data.totalRowCount)
            )

            break;
          case 'cases':
            // Cases page (General table of alerts Page)
            dispatch(

              // Update the UI to reflect the returned data
              setPageEvents(
                records
              )
            )

            dispatch(
              setTotalRowCount(response.data.totalRowCount)
            )

            break;
          default:
            console.error(`Unexpected target page ${target}`);
        }
      }
    }
  }
);

export const fetchAllAlertsByPage = createAsyncThunk<{exportEvents: { records: [], totalCount: number}}, {aFAccessToken: string, size: number, page: number, order: string, orderBy: string, filters: object, search: string}, { state: RootState, dispatch: AppDispatch }>(
  'fetchAllAlertsByPage',
  async ({aFAccessToken, size, page, order, orderBy, filters, search}, { getState, dispatch }: { getState: () => RootState, dispatch: AppDispatch }) => {
    const config = { 'Authorization': aFAccessToken || '', 'Content-Type': 'application/json' };

    let totalCount = 0;
    let records:any = [];

    do {
      const response_data = await getAlertsByPage(config, 1000, page, order, orderBy, filters, search);
      if (response_data && response_data.data.records) {
        const mapperResponse = getAlertsResponseMapper(t, response_data.data.records);

        records = records.length === 0 ? mapperResponse : [...records, ...mapperResponse]
        totalCount = response_data.data.totalRowCount;
      }
      page += 1;
    } while (records.length < totalCount);

    return { exportEvents: { records, totalCount } };
  }
);

/**
 * The Messages Redux Slice
 */
export const alertSlice = createSlice({
  name: 'alerts',
  initialState,
  reducers: {
    setWeeklyEvents: (state, action: PayloadAction<IRowProps[]>) => {
      const response = [...action.payload];
      state.weeklyEvents = response;
    },
    setPageEvents: (state, action: PayloadAction<IRowProps[]>) => {
      const response = [...action.payload];
      state.pageEvents = response;
    },
    putPageEvent: (state, action: PayloadAction<{value: string, id: string, type: string}>) => {
      state.pageEvents = state.pageEvents.map((item) => {
        if(action.payload.type === "email"){
          if (item.id === action.payload.id) {
            item.status = action.payload.value.trim() === "" ? t("STATUS.OPEN") as string : t("STATUS.IN_PROGRESS") as string;
            item.assignedto = action.payload.value;
          } 
        } else if (action.payload.type === "status") {
          if (item.id === action.payload.id) {
            item.status = action.payload.value.trim()
          } 
        }
        return item;
      })

      state.userEvents = state.userEvents.map((item) => {
        if(action.payload.type === "email"){
          if (item.id === action.payload.id) {
            item.status = action.payload.value.trim() === "" ? t("STATUS.OPEN") as string : t("STATUS.IN_PROGRESS") as string;
            item.assignedto = action.payload.value;
          } 
        } else if (action.payload.type === "status") {
          if (item.id === action.payload.id) {
            item.status = action.payload.value.trim()
          } 
        }
        return item;
      })
    },
    setConformanceMetrics: (state, action: PayloadAction<[]>) => {
      state.conformanceMetrics = action.payload;
    },
    setUserEvents: (state, action: PayloadAction<IRowProps[]>) => {
      state.userEvents = action.payload;
    },
    setAreUserEventsLoading: (state, action: PayloadAction<boolean>) => {
      state.areUserEventsLoading = action.payload;
    },
    setRecursionNewestTs: (state, action: PayloadAction<number>) => {
      state.newestStateEventTs = Number(action.payload);
    },
    setIsFetching: (state, action: PayloadAction<boolean>) => {
      state.isFetching = Boolean(action.payload);
    },
    setSideModal: (state, action: PayloadAction<boolean>) => {
      try {
        state.openSideModal = Boolean(action.payload);
        
        // If opening the side modal, set to a reasonable width if it was collapsed
        if (action.payload && state.previewWidth < 5) {
          state.previewWidth = 30;
        } else {
          if (action.payload === false) {
            state.previewWidth = 2;
          }
        }
      } catch (error) {
        console.error("Error in setSideModal:", error);
      }
    },
    setPreviewWidth: (state, action: PayloadAction<number>) => {
      try {
        state.previewWidth = action.payload;
      } catch (error) {
        console.error("Error in setPreviewWidth:", error);
      }
    },
    setTotalRowCount: (state, action: PayloadAction<number>) => {
      state.totalRowCount = action.payload;
    },
    setSelectedRow: (state, action: PayloadAction<string>) => {
      state.selectedRow = action.payload;
      console.log("Selected Row ID:", action.payload);
    },
    setExportLoading: (state, action: PayloadAction<boolean>) => {
      state.isExportLoading = action.payload;
    },
    updateWeeklyEvents: (state, action: PayloadAction<IRowProps[]>) => {
      if (action.payload.length > 0) {
        // Create a new state object to ensure immutability
        const newState = { ...state };
      
        // Iterate through each event in the payload
        action.payload.forEach(event => {

          // Find the corresponding row in newState.weeklyEvents based on the id
          const index = newState.weeklyEvents.findIndex(row => row.id === event.id);
          
          // If the row is found, update its status field
          if (index !== -1) {
            newState.weeklyEvents[index] = {
              ...newState.weeklyEvents[index],
              status: event.status,
              assignedto: event.assignedto
            };
          }
        });
      
        // Set the state
        state.weeklyEvents = newState.weeklyEvents;
      }
    },
    updateEvents: (state, action: PayloadAction<IRowProps[]>) => {
      if (action.payload.length > 0) {
        // Create a new state object to ensure immutability
        const newState = { ...state };
      
        // Iterate through each event in the payload
        action.payload.forEach(event => {

          // Find the corresponding row in newState.pageEvents based on the id
          const index = newState.pageEvents.findIndex(row => row.id === event.id);
          
          // If the row is found, update its status field
          if (index !== -1) {
            newState.pageEvents[index] = {
              ...newState.pageEvents[index],
              status: event.status,
              assignedto: event.assignedto
            };
          }
        });
      
        // Set the state
        state.pageEvents = newState.pageEvents;
      }
      state.isPageLoading = false;
    },
    setLastUpdated: (state, action: PayloadAction<number>) => {
      state.lastUpdatedTs = action.payload;
    },
    setStateFetching: (state, action: PayloadAction<boolean>) => {
      state.isFetching = action.payload;
    },
    setReduxFrameworkData: (state, action: PayloadAction<{
      descriptions?: any[],
      selectedDescription?: string,
      selectedRawRuleName?: string,
      selectedFramework?: string,
      selectedSectionTitle?: string
    }>) => {
      state.frameworkData = {
        ...state.frameworkData,
        ...action.payload
      };
    }
  },
  extraReducers: (builder) => {
    builder.addCase(assignUserToAlert.fulfilled, (state, action) => {
        // Print the outcome, better have a banner notice
        state.pageEvents = state.pageEvents.map((event) => {
          if (event.email === action.payload.email) {
            return({...event, assignedto: action.payload.email})
          } 
          return(event);
        })
      }
    );
    builder.addCase(fetchAllAlertsByPage.pending, (state) => {
      state.isFetching = true;
    });
    builder.addCase(fetchAllAlertsByPage.fulfilled, (state) => {
      state.isFetching = false;
    });
    builder.addCase(fetchAllAlertsByPage.rejected, (state) => {
      state.isFetching = false;
    });
    builder.addCase(updatePageAlerts.pending, (state) => {
      state.isFetching = true;
    });
    builder.addCase(updatePageAlerts.fulfilled, (state) => {
      state.isFetching = false;
    });
    builder.addCase(updatePageAlerts.rejected, (state) => {
      state.isFetching = false;
    });
    builder.addCase(setAlertsByPage.pending, (state) => {
      if (state.userEvents.length == 0) {
        state.areUserEventsLoading = true;
      }
    });
    builder.addCase(setAlertsByPage.fulfilled, (state) => {
      state.areUserEventsLoading = false;
    });
    builder.addCase(setAlertsByPage.rejected, (state) => {
      state.areUserEventsLoading = false;
    });
    builder.addCase(setWeeklyAlerts.pending, (state) => {
      state.isPageLoading = true;
    });
    builder.addCase(setWeeklyAlerts.fulfilled, (state) => {
      state.isPageLoading = false;
    });
    builder.addCase(setWeeklyAlerts.rejected, (state) => {
      state.isPageLoading = false;
    });
    builder.addCase(
      setAlertStatus.fulfilled, () => {
      },
    );
  },
});

export const { 
  setPageEvents,
  setWeeklyEvents,
  setConformanceMetrics,
  updateEvents,
  updateWeeklyEvents,
  putPageEvent,
  setStateFetching,
  setRecursionNewestTs,
  setIsFetching,
  setPreviewWidth,
  setLastUpdated,
  setSelectedRow,
  setTotalRowCount,
  setSideModal,
  setExportLoading,
  setUserEvents,
  setAreUserEventsLoading,
  setReduxFrameworkData
} = alertSlice.actions;

export default alertSlice.reducer;